/** Thin HTTP client for the auth, battle, friends and party endpoints. */

const BASE      = '/api/auth';
const BATTLE    = '/api/battle';
const FRIENDS   = '/api/friends';
const PARTY     = '/api/party';

/**
 * Single core for every auth/battle/friend/party request.
 * Centralises error logging: any failure — network error or non-OK response —
 * is printed to the console here before being rethrown, so no API exception is
 * ever silently swallowed regardless of how the caller handles it.
 */
async function request<T>(url: string, init: RequestInit): Promise<T> {
  try {
    const res  = await fetch(url, init);
    
    // Global 401 Interceptor: se a sessão expirar no meio do uso
    if (res.status === 401 && !url.includes('/verify') && !url.includes('/me')) {
      localStorage.removeItem('darkage_token');
      sessionStorage.setItem('session_expired', 'true');
      window.location.href = '/';
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }

    const data = (await res.json()) as T & { success?: boolean; error?: string };
    if (!res.ok || data.success === false) {
      throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
    }
    return data;
  } catch (err) {
    console.error(`[API] ${init.method ?? 'GET'} ${url} failed:`, err);
    throw err;
  }
}

function post<T>(path: string, body: unknown, token?: string, baseUrl = BASE): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return request<T>(`${baseUrl}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
}

function get<T>(path: string, token: string, baseUrl = BASE): Promise<T> {
  return request<T>(`${baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
}

function del<T>(path: string, token: string, baseUrl = BASE): Promise<T> {
  return request<T>(`${baseUrl}${path}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
}

// ─────────────────────────────────────────────────────────────────────────────

export interface FriendCharacter {
  id:    string;
  name:  string;
  class: string;
}

export interface FriendEntry {
  friendship_id: string;
  user_id:       string;
  email:         string;
  character:     FriendCharacter | null;
}

export interface FriendsList {
  friends:  FriendEntry[];
  received: FriendEntry[];
  sent:     FriendEntry[];
}

export const friendApi = {
  list:    (token: string)                    => get<FriendsList>('', token, FRIENDS),
  request: (token: string, query: string)     => post<{ success: boolean; status: string; friendship_id?: string; message?: string }>('/request', { query }, token, FRIENDS),
  accept:  (token: string, id: string)        => post<{ success: boolean }>(`/${id}/accept`, {}, token, FRIENDS),
  remove:  (token: string, id: string)        => del<{ success: boolean }>(`/${id}`, token, FRIENDS),
};

// ─────────────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id:          string;
  email:       string;
  role:        'user' | 'admin';
  language?:   string;
  characterId: string | null;
}

export interface Character {
  id:        string;
  userId:    string;
  name:      string;
  class:     string;
  createdAt: string;
  level?: number;
  currentXp?: number;
  unspentAttributePoints?: number;
  attributes?: Record<string, number>;
  totalPoints?: number;
  wins?: number;
  losses?: number;
  currentEnergy?: number;
  lastEnergyUpdate?: string;
  class_data?: {
    displayName: string;
    description: string;
    primaryElement: string;
    primaryAttribute: string;
    baseHp: number;
    baseAp: number;
    baseMp: number;
    inventoryCapacity: number;
  };
}

export const authApi = {
  /** Step 1 — send magic code to e-mail. In dev the code is returned as _dev_code. */
  requestCode: (email: string, turnstileToken: string | null) =>
    post<{ success: boolean; message: string; _dev_code?: string }>('/request', { email, turnstileToken }),

  /** Step 2 — validate magic code, receive session token. */
  verifyCode: (email: string, code: string) =>
    post<{ success: boolean; token: string; role: string; user: AuthUser }>('/verify', { email, code }),

  /** Resolve Bearer token → user profile. */
  me: (token: string) =>
    get<{ success: boolean; user: AuthUser }>('/me', token),

  /** Update user preferences. */
  updateMe: (token: string, data: { language?: string }) =>
    request<{ success: boolean; user: AuthUser }>(`${BASE}/me`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** Invalidate session. */
  logout: (token: string) =>
    post<{ success: boolean }>('/logout', {}, token),

  /** Create the one-and-only character for this user. */
  createCharacter: (token: string, name: string, cls: string) =>
    post<{ success: boolean; character: Character }>('/character', { name, class: cls }, token),

  /** Fetch current user's character. */
  getCharacter: (token: string) =>
    get<{ success: boolean; character: Character | null; game_config?: { maxEnergy: number; energyRechargeRate: number; battleEnergyCost: number } }>('/character', token),

  /** Upgrade character attribute. */
  upgradeAttribute: (token: string, attribute: string) =>
    post<{ success: boolean; unspent: number; value: number }>('/character/upgrade', { attribute }, token),

  /**
   * Start a new battle: the server picks a random map + random enemies,
   * spawns the player's character, and returns the combat/entity IDs.
   */
  startBattle: (token: string, fromBattlePoint?: boolean) =>
    post<{
      success: boolean;
      combat_id: string;
      player_entity_id: string;
      map_name: string;
      enemy_count: number;
    }>('/start', { from_battle_point: !!fromBattlePoint }, token, BATTLE),

  /** Refresh the user's online-presence timestamp (call every ~20 s). */
  heartbeat: (token: string) =>
    post<{ success: boolean }>('/heartbeat', {}, token),
};

// ─────────────────────────────────────────────────────────────────────────────

export interface PartyMemberProfile {
  user_id:   string;
  email:     string;
  character: { id: string; name: string; class: string } | null;
  is_online: boolean;
}

export interface PartyInvite {
  sent_at: number;
  user_id: string;
  email:   string;
  character: { id: string; name: string; class: string } | null;
  is_online: boolean;
}

export interface PartyNotice {
  type:    string;
  user_id: string;
  name:    string;
  at:      number;
}

export interface PartyData {
  id:              string;
  leader_id:       string;
  leader:          PartyMemberProfile;
  members:         PartyMemberProfile[];
  pending_invites: PartyInvite[];
  member_ids:      string[];
  all_members:     string[];
  size:            number;
  status:          'waiting' | 'in_combat';
  combat_id:       string | null;
  entity_map:      Record<string, string>;
  all_online:      boolean;
  my_entity_id:    string | null;
  notices:         PartyNotice[];
}

export interface PartyInviteBrief {
  id:     string;
  leader: PartyMemberProfile;
  size:   number;
  status: string;
}

export const partyApi = {
  /** Get current party state (or invite) for the authenticated user. */
  get: (token: string) =>
    get<{ party: PartyData | null; invite: PartyInviteBrief | null }>('', token, PARTY),

  /** Create a new party (disbands any existing one). */
  create: (token: string) =>
    post<{ success: boolean; party: PartyData }>('', {}, token, PARTY),

  /** Invite a friend by userId. */
  invite: (token: string, userId: string) =>
    post<{ success: boolean }>(`/invite/${userId}`, {}, token, PARTY),

  /** Accept the pending party invite. */
  accept: (token: string) =>
    post<{ success: boolean; party: PartyData }>('/accept', {}, token, PARTY),

  /** Decline the pending party invite. */
  decline: (token: string) =>
    del<{ success: boolean }>('/decline', token, PARTY),

  /** Leave the party (leader = disband). */
  leave: (token: string) =>
    del<{ success: boolean }>('/leave', token, PARTY),

  /** Kick a member (leader only). */
  kick: (token: string, userId: string) =>
    del<{ success: boolean }>(`/kick/${userId}`, token, PARTY),

  /** Launch co-op battle (leader only, all members must be online). */
  start: (token: string) =>
    post<{
      success:     boolean;
      combat_id:   string;
      entity_map:  Record<string, string>;
      map_name:    string;
      enemy_count: number;
    }>('/start', {}, token, PARTY),

  /** Acknowledge (clear) pending notices, e.g. "X declined your invite". */
  ackNotices: (token: string) =>
    del<{ success: boolean }>('/notices', token, PARTY),
};
